/**
 * api.js - Serviço de API com CORREÇÃO DEFINITIVA de URLs
 * 
 * ⚠️ PROBLEMA RESOLVIDO: VERSÃO ATUAL
 * URLs estavam sendo construídas com domínio de preview incorreto
 * 
 * ✅ SOLUÇÃO IMPLEMENTADA:
 * 1. SEMPRE usar window.location.origin em preview/produção
 * 2. NÃO confiar no .env para ambientes de deploy
 * 3. Auto-correção de URLs incorretas
 */

import axios from 'axios';

// ===== LISTA DE DOMÍNIOS INCORRETOS (para auto-correção) =====
const WRONG_DOMAINS = [
  'https://admin-panel-upgrade-27.preview.emergentagent.com',
  'http://secure-abri.preview.emergentagent.com',
  'https://admin-panel-upgrade-27.preview.emergentagent.com/',
  'https://admin-panel-upgrade-27.preview.emergentagent.com'
];

/**
 * 🔧 FUNÇÃO CRÍTICA - Obtém URL do backend em RUNTIME
 * Esta função é chamada TODA VEZ que uma URL é necessária
 * 
 * VERSÃO ATUAL: SEMPRE usar window.location.origin exceto em localhost
 */
const getBackendUrl = () => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }
  
  const hostname = window.location.hostname;
  const origin = window.location.origin;
  
  // Localhost = Desenvolvimento (único caso que usa .env)
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  }
  
  // 🔧 VERSÃO ATUAL: Preview/Produção = SEMPRE usar origin atual
  // NÃO confiar no .env pois pode estar desatualizado
  console.log('🌐 [api.js] Usando origin:', origin);
  return origin;
};

/**
 * 🔧 Função para corrigir URLs incorretas
 */
const fixUrl = (url) => {
  if (!url) return url;
  
  const correctBase = getBackendUrl();
  
  for (const wrongDomain of WRONG_DOMAINS) {
    if (url.includes(wrongDomain.replace(/\/$/, ''))) {
      const fixed = url.replace(wrongDomain.replace(/\/$/, ''), correctBase);
      console.warn('🔧 [api.js] URL AUTO-CORRIGIDA:', url, '→', fixed);
      return fixed;
    }
  }
  
  return url;
};

// ===== CRIAR INSTÂNCIA AXIOS =====
// ⚠️ IMPORTANTE: Usar função getter para baseURL, não constante
const api = axios.create({
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: false,
});

// ===== INTERCEPTOR DE REQUEST - CORREÇÃO AUTOMÁTICA =====
api.interceptors.request.use(
  (config) => {
    // 1. Obter URL base correta em RUNTIME
    const correctBaseUrl = getBackendUrl();
    
    // 2. Se a URL é relativa, adicionar base correta
    if (config.url && !config.url.startsWith('http')) {
      // URL relativa - adicionar base
      if (!config.url.startsWith('/api')) {
        config.url = `/api${config.url.startsWith('/') ? '' : '/'}${config.url}`;
      }
      config.baseURL = correctBaseUrl;
    } else if (config.url) {
      // URL absoluta - verificar e corrigir se necessário
      config.url = fixUrl(config.url);
    }
    
    // 3. Corrigir baseURL se estiver errada
    if (config.baseURL) {
      config.baseURL = fixUrl(config.baseURL);
    }
    
    // 4. Adicionar token de autenticação
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Log para debug
    console.log(`📤 [api.js] Request: ${config.method?.toUpperCase()} ${config.baseURL || ''}${config.url}`);
    
    return config;
  },
  (error) => {
    console.error('❌ [api.js] Request error:', error);
    return Promise.reject(error);
  }
);

// ===== INTERCEPTOR DE RESPONSE =====
// 🔧 VERSÃO ATUAL: Retry automático para erros de conexão (520, 502, timeout)
api.interceptors.response.use(
  (response) => {
    console.log(`📥 [api.js] Response: ${response.status} ${response.config.url}`);
    return response;
  },
  async (error) => {
    const config = error.config;
    
    // 🔧 8.1: Não fazer retry se já tentamos ou é uma request de upload
    if (config._retry || config.url?.includes('/upload') || config.url?.includes('/audio')) {
      if (error.response) {
        console.error(`❌ [api.js] Error ${error.response.status}:`, error.response.data);
      } else if (error.request) {
        console.error('❌ [api.js] Network Error:', error.message);
      } else {
        console.error('❌ [api.js] Error:', error.message);
      }
      return Promise.reject(error);
    }
    
    // 🔧 8.1: Verificar se é erro de conexão que vale retry
    const shouldRetry = (
      !error.response || // Sem resposta = erro de rede
      error.response.status === 520 || // Cloudflare error
      error.response.status === 502 || // Bad Gateway
      error.response.status === 503 || // Service Unavailable
      error.response.status === 504 || // Gateway Timeout
      error.code === 'ECONNABORTED' // Timeout
    );
    
    if (shouldRetry) {
      config._retry = true;
      console.warn(`🔄 [api.js] Erro de conexão, tentando novamente em 2s...`);
      
      // Aguardar 2 segundos antes de tentar novamente
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      return api(config);
    }
    
    if (error.response) {
      console.error(`❌ [api.js] Error ${error.response.status}:`, error.response.data);
    } else if (error.request) {
      console.error('❌ [api.js] Network Error:', error.message);
    } else {
      console.error('❌ [api.js] Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// ===== EXPORTAR FUNÇÃO getBackendUrl =====
export { getBackendUrl, fixUrl };

// ===== AUTH API =====
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/me', data),
  checkSession: () => api.get('/auth/session'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  // 🔄 NOVO: Forçar atualização da sessão após pagamento/upgrade
  forceRefreshSession: () => api.post('/auth/force-refresh'),
};

// ===== WORKS API =====
export const worksAPI = {
  create: (data) => api.post('/works', data),
  list: (params) => api.get('/works', { params }),
  get: (id) => api.get(`/works/${id}`),
  update: (id, data) => api.put(`/works/${id}`, data),
  delete: (id) => api.delete(`/works/${id}`),
  archive: (id) => api.post(`/works/${id}/archive`),
  unarchive: (id) => api.post(`/works/${id}/unarchive`),
  togglePublic: (id, isPublic) => api.put(`/works/${id}`, { public_gallery: isPublic }),
  uploadFile: (id, file, fileType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);
    return api.post(`/works/${id}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  uploadAudio: (id, formData) => {
    return api.post(`/works/${id}/audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 120000,
    });
  },
  // 🗑️ VERSÃO ATUAL: Excluir áudio de uma obra
  deleteAudio: (id) => api.delete(`/works/${id}/audio`),
  downloadFile: (workId, gridfsId) => 
    api.get(`/works/${workId}/files/${gridfsId}`, { responseType: 'blob' }),
  generateCertificate: (id) => api.post(`/works/${id}/certificate`),
  downloadCertificate: (id) => 
    api.get(`/works/${id}/certificate/pdf`, { responseType: 'blob' }),
};

// ===== PUBLIC API =====
export const publicAPI = {
  verifyCertificate: (hash) => api.get(`/verify/${hash}`),
};

// ===== LOG INICIAL =====
if (typeof window !== 'undefined') {
  console.log('🌐 [api.js] Serviço de API inicializado');
  console.log('   Hostname:', window.location.hostname);
  console.log('   Origin:', window.location.origin);
  console.log('   Backend URL:', getBackendUrl());
}

export default api;
